import React, { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { last } from 'lodash-es';
import { MessageDescriptor } from 'react-intl';
import {
  createHtmlPortalNode,
  InPortal,
  OutPortal,
} from 'react-reverse-portal';

import { ReactQueryKeys } from '../../../../../common/react-query-keys';
import { User } from '../../../../../models/User';
import { WhatsappVerificationStep } from '../../../../WhatsappVerification/types';
import { useWhatsappVerificationContext } from '../../../../WhatsappVerification/WhatsappVerificationContext/WhatsappVerificationContext';
import ConfirmNumberModalFooter from './components/ConfirmNumberModalFooter';
import { InputNumberModalFooter } from './components/InputNumberModalFooter';
import { InputOTPModalFooter } from './components/InputOTPModalFooter';
import { VerificationSuccessModalFooter } from './components/VerificationSuccessModalFooter';

type ModalFooterProps = {
  handleClose: () => Promise<void>;
  successButtonText: MessageDescriptor;
  closeButtonText?: MessageDescriptor;
};

export const ModalFooter = ({
  handleClose,
  successButtonText,
  closeButtonText,
}: ModalFooterProps) => {
  const { currentStep } = useWhatsappVerificationContext();
  const confirmNumberPortalNode = useMemo(() => createHtmlPortalNode(), []);

  const queryCLient = useQueryClient();
  function checkShouldDisableCloseButton() {
    const lastUpdatedUserData = last(
      queryCLient.getMutationCache().findAll({
        mutationKey: ReactQueryKeys.updatePhone(),
      })
    )?.state.data as User | null;

    if (!lastUpdatedUserData) {
      return false;
    }

    return !lastUpdatedUserData.isWhatsappVerified;
  }
  return (
    <>
      <InPortal node={confirmNumberPortalNode}>
        <ConfirmNumberModalFooter onClose={handleClose} />
      </InPortal>
      <Choose>
        <When
          condition={
            currentStep === WhatsappVerificationStep.VerificationSuccess
          }
        >
          <VerificationSuccessModalFooter
            onClose={handleClose}
            buttonTitle={successButtonText}
          />
        </When>
        <When condition={currentStep === WhatsappVerificationStep.InputOTP}>
          <InputOTPModalFooter />
        </When>
        <When condition={currentStep === WhatsappVerificationStep.InputNumber}>
          <InputNumberModalFooter
            onClose={handleClose}
            closeButtonText={closeButtonText}
            closeButtonProps={{
              disabled: checkShouldDisableCloseButton(),
            }}
          />
        </When>
        <When
          condition={
            currentStep === WhatsappVerificationStep.ConfirmWhatsappNumber
          }
        >
          <OutPortal node={confirmNumberPortalNode} />
        </When>
      </Choose>
    </>
  );
};
